import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Star, FileText, Globe, Share2, MapPin, Layout, ChevronRight,
  CheckCircle2, Circle, Loader2, ArrowRight, Target, Play, BarChart3,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAP_ICONS = {
  reviews: Star,
  blog_content: FileText,
  faq_schema: Globe,
  directory_listings: MapPin,
  social_media: Share2,
  gbp_completeness: Layout,
  website_content: FileText,
};

const GAP_COLORS = {
  reviews: '#C67A5C',
  blog_content: '#1D6B4F',
  faq_schema: '#0A66C2',
  directory_listings: '#EA4335',
  social_media: '#E1306C',
  gbp_completeness: '#FBBC05',
  website_content: '#1D6B4F',
};

export default function GapCloser() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [doAllLoading, setDoAllLoading] = useState(false);
  const [doAllResult, setDoAllResult] = useState(null);
  const [expandedGap, setExpandedGap] = useState(null);

  const fetchGaps = async () => {
    try {
      const res = await axios.get(`${API}/gaps`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGaps(); }, []);

  const handleDoAll = async () => {
    setDoAllLoading(true);
    try {
      const res = await axios.post(`${API}/gaps/do-all`, {}, { withCredentials: true });
      setDoAllResult(res.data);
      await fetchGaps();
    } catch {}
    setDoAllLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.has_business) {
    return (
      <AppShell active="optimize">
        <div className="p-5 text-center py-20">
          <p className="text-sm text-[#5C5347]">Complete your scan first to see gaps holding you back.</p>
        </div>
      </AppShell>
    );
  }

  const gaps = data.gaps || [];
  const doAll = data.do_all || {};
  const closedCount = gaps.filter(g => g.progress >= 100).length;

  return (
    <AppShell active="optimize">
      <div className="p-5 max-w-3xl mx-auto pb-28" data-testid="gap-closer-page">
        <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight mb-1">
          Close the Gap
        </h1>
        <p className="text-sm text-[#5C5347] mb-4">
          {gaps.length} gaps found — fix them to boost your score by ~{doAll.estimated_score_impact} pts
        </p>

        {/* Overall progress */}
        <div className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] mb-4" data-testid="gap-progress-bar">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-[#5C5347]">Gaps closed</span>
            <span className="font-semibold text-[#3C3226]">{closedCount}/{gaps.length}</span>
          </div>
          <div className="h-2.5 bg-[#F7F4EF] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1D6B4F] rounded-none transition-all duration-700"
              style={{ width: `${gaps.length > 0 ? (closedCount / gaps.length) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Do All Master Button */}
        {doAll.available && !doAllResult && (
          <motion.button
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={handleDoAll}
            disabled={doAllLoading}
            className="w-full p-5 rounded-sm text-[#F0EBE3] flex items-center gap-4 transition-opacity hover:opacity-85 active:scale-[0.98] mb-5 text-left disabled:opacity-70"
            style={{ backgroundColor: '#2C2520' }}
            data-testid="do-all-btn"
          >
            <div className="w-12 h-12 rounded-none bg-[#F0EBE3]/15 flex items-center justify-center flex-shrink-0">
              {doAllLoading
                ? <Loader2 size={22} className="text-[#F0EBE3] animate-spin" />
                : <Zap size={22} className="text-[#F0EBE3]" />}
            </div>
            <div className="flex-1">
              <span className="text-base font-medium block">
                {doAllLoading ? 'Generating content...' : 'Fix Everything We Can'}
              </span>
              <span className="text-sm text-[#F0EBE3]/70">
                {doAll.auto_actions} auto-fixes + {doAll.manual_actions} guided steps — ~{doAll.estimated_minutes} min
              </span>
            </div>
            <Play size={20} className="text-[#F0EBE3]/60 flex-shrink-0" />
          </motion.button>
        )}

        {/* Do All Results */}
        <AnimatePresence>
          {doAllResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-sm bg-[#1D6B4F]/5 border border-[#1D6B4F]/20 mb-5"
              data-testid="do-all-results"
            >
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={18} className="text-[#1D6B4F]" />
                <h3 className="text-sm font-medium text-[#3C3226]">Auto-fixes complete!</h3>
              </div>
              <div className="space-y-1.5 mb-3">
                {doAllResult.actions_completed?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-[#5C5347]">
                    <CheckCircle2 size={12} className="text-[#1D6B4F]" />
                    {a.message}
                  </div>
                ))}
              </div>
              {doAllResult.manual_todos?.length > 0 && (
                <>
                  <p className="text-xs font-medium text-[#C67A5C] mb-2">Still needs your attention:</p>
                  {doAllResult.manual_todos.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(t.path)}
                      className="w-full text-left p-2.5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8] mb-1.5 flex items-center gap-2 hover:bg-[#EDE8DF] transition-colors"
                    >
                      <Circle size={10} className="text-[#C67A5C] flex-shrink-0" />
                      <span className="text-xs text-[#3C3226] flex-1">{t.message}</span>
                      <span className="text-[10px] text-[#5C5347]">~{t.minutes} min</span>
                      <ChevronRight size={12} className="text-[#5C5347]" />
                    </button>
                  ))}
                </>
              )}
              <p className="text-xs text-[#1D6B4F] font-medium mt-2">
                Estimated score boost: +{doAllResult.estimated_score_impact} points
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Individual Gap Cards */}
        <div className="space-y-3">
          {gaps.map((gap) => {
            const Icon = GAP_ICONS[gap.type] || Target;
            const color = GAP_COLORS[gap.type] || '#1D6B4F';
            const isExpanded = expandedGap === gap.type;
            const isDone = gap.progress >= 100;

            return (
              <motion.div
                key={gap.type}
                layout
                className={`rounded-none border overflow-hidden transition-all ${
                  isDone
                    ? 'bg-[#1D6B4F]/5 border-[#1D6B4F]/15'
                    : 'bg-[#F7F4EF] border-[#D4C9B8]'
                }`}
                data-testid={`gap-${gap.type}`}
              >
                <button
                  onClick={() => setExpandedGap(isExpanded ? null : gap.type)}
                  className="w-full p-4 flex items-center gap-3 text-left"
                >
                  <div
                    className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    {isDone
                      ? <CheckCircle2 size={18} className="text-[#1D6B4F]" />
                      : <Icon size={18} style={{ color }} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#3C3226]">{gap.label}</p>
                      {gap.auto_fixable && !isDone && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#1D6B4F]/10 text-[#1D6B4F]">AUTO</span>
                      )}
                    </div>
                    <p className="text-xs text-[#5C5347] truncate">{gap.message}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs font-medium" style={{ color: isDone ? '#1D6B4F' : '#C67A5C' }}>
                      +{gap.score_impact} pts
                    </span>
                    <ChevronRight size={14} className={`text-[#5C5347] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </button>

                {/* Progress bar */}
                {gap.progress > 0 && (
                  <div className="px-4 pb-2">
                    <div className="h-1 bg-[#F7F4EF] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-none transition-all duration-500"
                        style={{
                          width: `${gap.progress}%`,
                          backgroundColor: isDone ? '#1D6B4F' : color,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 border-t border-[#D4C9B8] pt-3">
                        {/* Gap-specific resolution steps */}
                        <GapResolution gap={gap} navigate={navigate} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {gaps.length === 0 && (
          <div className="text-center py-12">
            <CheckCircle2 size={40} className="text-[#1D6B4F] mx-auto mb-3" />
            <h2 className="text-lg font-medium text-[#3C3226] mb-1">All gaps closed!</h2>
            <p className="text-sm text-[#5C5347]">You're ahead of the competition. Keep it up.</p>
          </div>
        )}
      </div>
    </AppShell>
  );
}


/* ── Gap Resolution Steps ── */
function GapResolution({ gap, navigate }) {
  const steps = getStepsForGap(gap);

  return (
    <div className="space-y-2">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-[#F7F4EF] flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-[10px] font-bold text-[#5C5347]">{i + 1}</span>
          </div>
          <div className="flex-1">
            <p className="text-xs text-[#3C3226] font-medium">{step.text}</p>
            {step.link && (
              <button
                onClick={() => step.external ? window.open(step.link, '_blank') : navigate(step.link)}
                className="text-xs text-[#1D6B4F] font-medium flex items-center gap-1 mt-0.5 hover:underline"
              >
                {step.linkText || 'Go'} {step.external ? <ExternalLink size={10} /> : <ArrowRight size={10} />}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function getStepsForGap(gap) {
  switch (gap.type) {
    case 'reviews':
      return [
        { text: 'Go to your Dashboard and tap "Ask for a review"', link: '/dashboard', linkText: 'Open Dashboard' },
        { text: `Send ${Math.min(gap.gap, 5)} review requests to recent happy customers` },
        { text: 'Each review adds ~2-3 points to your AI recommendation score' },
      ];
    case 'blog_content':
      return [
        { text: 'Open the Content Engine to generate AI-optimized posts', link: '/content', linkText: 'Open Content' },
        { text: `Review and publish ${gap.gap > 4 ? 4 : gap.gap} pending posts${gap.pending_approval > 0 ? ` (${gap.pending_approval} already ready!)` : ''}` },
        { text: 'Each published post gives AI more data to cite you' },
      ];
    case 'faq_schema':
      return [
        { text: 'Get your Schema.org JSON-LD markup', link: '/optimize', linkText: 'Get Schema' },
        { text: 'Copy the code and paste it into your website\'s <head> section' },
        { text: 'AI is 3x more likely to mention businesses with structured FAQ content' },
      ];
    case 'directory_listings':
      return [
        { text: 'Open the Optimize page to see all directories', link: '/optimize', linkText: 'View Directories' },
        { text: 'Start with Google Business Profile — it\'s the #1 source for AI', link: 'https://business.google.com/', linkText: 'Open GBP', external: true },
        { text: 'Then add Yelp, Apple Maps, and Bing Places' },
        { text: 'Mark each as "Done" after submitting' },
      ];
    case 'social_media':
      return [
        { text: 'Go to Content and approve social media posts', link: '/content', linkText: 'Open Content' },
        { text: 'Copy and paste each post to your platforms' },
        { text: 'See our posting guides for step-by-step help', link: '/guides/facebook', linkText: 'View Guides' },
      ];
    case 'gbp_completeness':
      return [
        { text: 'Open Google Business Profile', link: 'https://business.google.com/', linkText: 'Open GBP', external: true },
        ...(gap.missing_items || []).map(item => ({ text: `Add ${item} to your profile` })),
        { text: 'A complete GBP gets 7x more clicks from AI results' },
      ];
    case 'website_content':
      return [
        { text: 'Generate service pages and blog posts', link: '/content', linkText: 'Open Content' },
        { text: 'Add pages for each service you offer' },
        { text: 'Include your city name and profession on every page' },
      ];
    default:
      return [{ text: 'Take action to close this gap' }];
  }
}
